// FLAGS: health-data, silent-failure
// Creates a back-dated consult record for a client, or edits an existing one's expert, language and
// type; submit failures are swallowed by a try/finally with no catch and no message.
import React, { useCallback, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogActions } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useFormik, FormikHelpers } from "formik";
import dayjs from "dayjs";
import Button from "@openup/shared/components/Button/Button";
import {
  withSnackbar,
  WithSnackbarProps,
} from "@src/components/SnackBarComponent";
import { TypographyWithIcon } from "@src/components/utils/TypographyWithIcon";
import { validationSchemaSessionDetails } from "@src/utils/validationHelpers";
import { LANGUAGE_TYPES } from "@src/constants";
import { useConsultMutation } from "@src/queries/consults";
import { getCurrentUser } from "@src/services/authentication.service";
import { Consult } from "@src/models";
import Footer from "./components/Footer";
import Header from "./components/Header";
import SessionDetailsEditing from "./SessionDetailsEditing";
import CancelWhileEditModal from "./components/CancelWhileEditModal";

interface EditSessionDetailsDialogProps extends WithSnackbarProps {
  isOpen?: boolean;
  clientId: string;
  consult?: Consult;
  refetch?: () => void;
  onClose?: () => void;
}

interface SessionDetailsFormValues {
  startedAt: string;
  userId: string;
  employeeId: string;
  status: string;
  consultType: string;
  consultLanguage: string;
  employeeName: string;
  clientTimezone: string;
}

const EditSessionDetailsDialog: React.FC<EditSessionDetailsDialogProps> = ({
  isOpen = false,
  clientId,
  consult,
  refetch = () => {},
  onClose = () => {},
}) => {
  const { t } = useTranslation();
  const currentUser = getCurrentUser();

  const [isCancelEditingDialog, setIsCancelEditingDialog] = useState(false);

  const {
    updateConsult: { mutateAsync: updateConsult },
    createConsult: { mutateAsync: createConsult },
  } = useConsultMutation(clientId);

  const getCorrectStartedAt = useCallback((): string => {
    if (consult) {
      return dayjs(consult.startedAt).toISOString();
    }
    const start = dayjs().second(0);
    const remainder = start.minute() % 30;
    return start.add(-remainder, "minute").toISOString();
  }, [consult]);

  const startedAt = useMemo(() => getCorrectStartedAt(), [getCorrectStartedAt]);

  const formikDetails = useFormik<SessionDetailsFormValues>({
    enableReinitialize: true,
    validationSchema: validationSchemaSessionDetails,
    initialValues: {
      startedAt,
      userId: consult?.user.id ?? clientId,
      employeeId: consult?.employee.id ?? currentUser!.id,
      status: consult?.status ?? "Completed",
      consultType: consult?.consultType ?? "general",
      consultLanguage: consult?.consultLanguage ?? LANGUAGE_TYPES.ENGLISH_GB,
      employeeName: consult?.employeeFullName ?? "",
      clientTimezone: consult?.clientTimezone ?? "Europe/Amsterdam",
    },
    onSubmit: async (
      values: SessionDetailsFormValues,
      { setSubmitting }: FormikHelpers<SessionDetailsFormValues>,
    ) => {
      values.startedAt = dayjs(values.startedAt).toISOString();

      try {
        setSubmitting(true);
        if (!consult?.id) {
          await createConsult({ ...values });
          refetch();
        } else {
          await updateConsult({ ...values, id: consult.id });
          refetch();
        }
        setIsCancelEditingDialog(false);
        onClose();
      } finally {
        setSubmitting(false);
      }
    },
  });

  const handleCancelButtonDialog = () => {
    if (!formikDetails.dirty) {
      onClose();
    } else {
      setIsCancelEditingDialog(true);
    }
  };

  const handleCancelWithoutSaving = () => {
    formikDetails.resetForm();
    setIsCancelEditingDialog(false);
    onClose();
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === "Escape") handleCancelButtonDialog();
  };

  const onSubmitMainDialog = async () => {
    if (!formikDetails.dirty && !!consult) {
      onClose();
      return;
    }
    await formikDetails.submitForm();
  };

  return (
    <Dialog
      open={isOpen}
      scroll="paper"
      classes={{ paper: "absolute top-[4vh] max-h-[92vh] w-[45vw] p-0" }}
      onKeyDown={handleKeyPress}
      data-cy="session-details-editing"
    >
      <Header
        headerStyle=""
        rightChildren={<div />}
        centerChildren={<div />}
        leftChildren={
          <TypographyWithIcon
            variant="h6"
            iconLigature="question_answer"
            boxClass="pt-3 pl-2"
          >
            {t("Consults.SessionDetails")}
          </TypographyWithIcon>
        }
      />
      <DialogContent dividers className="p-0">
        <SessionDetailsEditing
          formikDetails={formikDetails}
          isNewConsult={!consult}
        />
      </DialogContent>
      <DialogActions style={{ justifyContent: "space-between" }}>
        <Footer
          footerStyle=""
          leftChildren={
            <Button variant="secondary" onClick={handleCancelButtonDialog}>
              {t("Common.Cancel")}
            </Button>
          }
          centerChildren={<div />}
          rightChildren={
            <Button
              disabled={
                formikDetails.isSubmitting ||
                !validationSchemaSessionDetails.isValidSync(
                  formikDetails.values,
                )
              }
              variant="primary"
              onClick={onSubmitMainDialog}
              type="submit"
            >
              {t("Common.Submit")}
            </Button>
          }
        />
      </DialogActions>
      <CancelWhileEditModal
        isOpen={isCancelEditingDialog}
        handleCancelWithoutSaving={handleCancelWithoutSaving}
        handleContinueEditing={() => setIsCancelEditingDialog(false)}
      />
    </Dialog>
  );
};

export default withSnackbar(EditSessionDetailsDialog);
